import { Stack } from '@mui/material';
import type {
  BackupVault,
  InventoryResource,
  RdsNativePitrWindow,
  Snapshot,
} from '@repo/api-gw-sdk';
import { ResourceType } from '@repo/api-gw-sdk';
import React, { useCallback, useEffect, useState } from 'react';

import { isNativeBackupResource } from '@/app/(dashboard)/inventory/inventoryResourceUtils';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { isDateValid } from '@/utils/dateTime';
import { dayjs } from '@/utils/dayjs';

import { ContinuousSnapshotRow } from './continuousSnapshotRow';
import { EfsSnapshots } from './efsSnapshots';
import type { MutateSnapshots } from './eonSnapshotsTab';
import { FsxSnapshots } from './fsxSnapshots';
import { PitrSnapshots } from './pitrSnapshots';
import { SnapshotDrawerHeader } from './snapshotDrawerHeader';
import type { SnapshotsOrigin } from './snapshotOriginToggleButtons';
import { SnapshotsRows } from './snapshotsRows';
import type { SnapshotsType } from './snapshotsTypesTabs';
import { VmBasedSnapshots } from './vmBasedSnapshots';

import { OtherSnapshotsList } from '../../../../snapshots/convertToEon/otherSnapshotsList';
import { Panels } from '../../../panels';
import type { PanelTypes } from '../../../panelsMap';

interface RestoreWizardArgs {
  snapshot: Snapshot;
  resource: InventoryResource;
  onClose: () => void;
  onBack: () => void;
}

export type RestoreWizardFactory = (args: RestoreWizardArgs) => PanelTypes;

type RestoreWizardConfig =
  | (typeof Panels)[keyof typeof Panels]
  | RestoreWizardFactory;

const restoreWizardConfigs: Partial<Record<ResourceType, RestoreWizardConfig>> =
  {
    [ResourceType.AwsS3]: Panels.RestoreS3Wizard,
    [ResourceType.AzureStorageAccount]: Panels.RestoreS3Wizard,
    [ResourceType.GcpCloudStorageBucket]: Panels.RestoreS3Wizard,
    [ResourceType.AwsRds]: Panels.RestoreRDSWizard,
    [ResourceType.AwsDynamoDb]: Panels.RestoreDynamoWizard,
    [ResourceType.AtlasMongodbCluster]: Panels.RestoreMongoAtlasWizard,
    [ResourceType.AwsEksNamespace]: Panels.RestoreEksNamespaceWizard,
    [ResourceType.AzureSqlManagedInstance]: Panels.RestoreAzureSqlDBWizard,
    [ResourceType.AzureSqlDatabase]: Panels.RestoreAzureSqlDBWizard,
    [ResourceType.AzureSqlVirtualMachine]: Panels.RestoreAzureVMWizard,
    [ResourceType.AzureSapHanaVm]: Panels.RestoreAzureVMWizard,
    [ResourceType.GcpCloudSqlInstance]: Panels.RestoreGcpCloudSqlWizard,
    [ResourceType.GcpBigQuery]: Panels.RestoreBigQueryWizard,
    [ResourceType.GcpDisk]: Panels.RestoreGcpDiskWizard,
    [ResourceType.AwsEbsVolume]: (args) => ({
      panel: Panels.RestoreVolumeWizard,
      props: {
        ...args,
        actionType: 'volume',
        providerVolumeId:
          args.snapshot.resource?.properties?.awsEbsVolume?.volume
            ?.providerVolumeId || '',
      },
    }),
  };

const getRestoreWizardFactory = (
  resourceType: ResourceType
): RestoreWizardFactory | undefined => {
  const config = restoreWizardConfigs[resourceType];
  if (!config) return undefined;
  if (typeof config === 'function') return config;
  return (args) => ({ panel: config, props: args }) as PanelTypes;
};

const VmBasedResourceTypes: ResourceType[] = [
  ResourceType.AwsEc2,
  ResourceType.AzureVirtualMachine,
  ResourceType.AzureSqlVirtualMachine,
  ResourceType.AzureSapHanaVm,
  ResourceType.GcpComputeEngineInstance,
];

export const RestoreSnapshotDrawer = ({
  vaults,
  eonSnapshots,
  hasProviderSnapsInDate,
  showOtherSnapshots,
  continuousSnapshots,
  selectedDate,
  entity,
  onClose,
  mutateSnapshots,
}: {
  vaults: BackupVault[];
  eonSnapshots: Snapshot[];
  hasProviderSnapsInDate: boolean;
  showOtherSnapshots: boolean;
  continuousSnapshots: Partial<RdsNativePitrWindow> | undefined;
  selectedDate: Date;
  entity: InventoryResource;
  onClose: () => void;
  mutateSnapshots: MutateSnapshots;
}) => {
  const {
    AzureSqlVirtualMachineSupport,
    AzureSnapshotsDiscoverySupport,
    RdsPitrEnabled,
  } = useFeatureFlags();

  const isContinuousSupported =
    RdsPitrEnabled &&
    continuousSnapshots?.earliestRestorableTime &&
    continuousSnapshots?.latestRestorableTime &&
    dayjs
      .utc(selectedDate)
      .endOf('day')
      .isAfter(
        dayjs.utc(continuousSnapshots?.earliestRestorableTime).startOf('day')
      ) &&
    dayjs
      .utc(selectedDate)
      .startOf('day')
      .isBefore(
        dayjs.utc(continuousSnapshots?.latestRestorableTime).endOf('day')
      );

  const [selectedOrigin, setSelectedOrigin] = useState<SnapshotsOrigin>(
    hasProviderSnapsInDate ? 'other' : 'eon'
  );
  const isVmBaseResourceType = VmBasedResourceTypes.includes(
    entity.resourceType
  );
  const isFsxResourceType = entity.resourceType === ResourceType.AwsFsx;
  const isEfsResourceType = entity.resourceType === ResourceType.AwsEfs;
  const isNativeBackup = isNativeBackupResource(entity);
  const getSnapshotType = useCallback(() => {
    return isVmBaseResourceType
      ? 'vm-based'
      : isContinuousSupported
        ? 'rds-based-snapshots'
        : undefined;
  }, [isVmBaseResourceType, isContinuousSupported]);
  const [snapshotType, setSnapshotType] = useState<SnapshotsType | undefined>(
    getSnapshotType()
  );
  const [selectedDateSnapshots, setSelectedDateSnapshots] = useState<
    Snapshot[]
  >([]);
  const [selectedSnapshot, setSelectedSnapshot] = React.useState<string>('');

  const restoreWizardFactory = getRestoreWizardFactory(entity.resourceType);

  useEffect(() => {
    const dateSnapshots = selectedDate
      ? (eonSnapshots ?? [])
          .filter((e) => {
            const date = isDateValid(e.pointInTime)
              ? e.pointInTime
              : e.createdTime;

            return dayjs.utc(date).isSame(dayjs.utc(selectedDate), 'date');
          })
          .sort((a, b) => {
            const dateA = isDateValid(a.pointInTime)
              ? a.pointInTime
              : a.createdTime;

            const dateB = isDateValid(b.pointInTime)
              ? b.pointInTime
              : b.createdTime;

            return dateB.getTime() - dateA.getTime();
          })
      : [];
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedDateSnapshots(dateSnapshots);
    if (dateSnapshots.length <= 0) {
      setSelectedOrigin('other');
    }
  }, [selectedDate, eonSnapshots]);

  useEffect(() => {
    if (snapshotType === 'vm-based') {
      const snapshotsWithoutPiTR = selectedDateSnapshots.filter(
        (snap) => !snap.supportPiTR
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedSnapshot(snapshotsWithoutPiTR[0]?.id || '');
    } else if (snapshotType === 'database-pitr') {
      const snapshotsWithPiTR = selectedDateSnapshots.filter(
        (snap) => snap.supportPiTR
      );
      setSelectedSnapshot(snapshotsWithPiTR[0]?.id || '');
    } else if (isNativeBackup) {
      setSelectedSnapshot(selectedDateSnapshots[0]?.id || '');
    }
  }, [selectedDateSnapshots, snapshotType, isNativeBackup]);

  const shouldShowGroupButtons = !!(
    selectedDateSnapshots.length &&
    hasProviderSnapsInDate &&
    showOtherSnapshots
  );

  const snapshotsWithoutPiTR = selectedDateSnapshots.filter(
    (snap) => !snap.supportPiTR
  );

  const snapshotsWithPiTR = selectedDateSnapshots.filter(
    (snap) => snap.supportPiTR
  );

  const shouldShowSnapsTypeTabs =
    (AzureSqlVirtualMachineSupport &&
      isVmBaseResourceType &&
      snapshotsWithPiTR.length > 0) ||
    isContinuousSupported;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSnapshotType(getSnapshotType());
  }, [getSnapshotType, shouldShowSnapsTypeTabs]);

  const isContainerHeightFixed =
    snapshotType === 'database-pitr' && !isContinuousSupported;

  return (
    <Stack
      maxHeight={isContainerHeightFixed ? 'none' : '60%'}
      flexBasis={isContainerHeightFixed ? '60%' : 'fit-content'}
      flexGrow={isContainerHeightFixed ? '0' : undefined}
      flexShrink={isContainerHeightFixed ? '0' : undefined}
      overflow={isContainerHeightFixed ? 'hidden' : undefined}
      data-testid='restore-snapshot-drawer'
      className='w-full'
      direction='column'
      boxShadow='var(--mui-customShadows-panelMd)'
    >
      <Stack
        overflow='hidden'
        height='100%'
        className='w-full'
        direction='column'
      >
        <SnapshotDrawerHeader
          selectedDate={selectedDate}
          shouldShowGroupButtons={shouldShowGroupButtons}
          onSnapshotTypeSelected={setSnapshotType}
          shouldShowSnapsTypeTabs={shouldShowSnapsTypeTabs}
          snapshotType={snapshotType}
          onClose={onClose}
          onSnapshotOriginSelected={setSelectedOrigin}
          selectedOrigin={selectedOrigin}
          resourceType={entity.resourceType}
        />
        <Stack
          direction='column'
          alignItems='center'
          width='100%'
          minHeight='0'
          flexGrow='1'
        >
          {selectedOrigin === 'other' &&
          showOtherSnapshots &&
          hasProviderSnapsInDate &&
          (entity.resourceType === ResourceType.AwsEc2 ||
            isNativeBackup ||
            (entity.resourceType === ResourceType.AzureVirtualMachine &&
              AzureSnapshotsDiscoverySupport)) ? (
            <OtherSnapshotsList
              data-testid='other-snapshots-tab'
              resource={entity}
              selectedDate={selectedDate}
            />
          ) : (
            <>
              {snapshotType === 'vm-based' && (
                <VmBasedSnapshots
                  snapshots={snapshotsWithoutPiTR}
                  entity={entity}
                  selectedSnapshot={selectedSnapshot}
                  setSelectedSnapshot={setSelectedSnapshot}
                  vaults={vaults}
                  mutateSnapshots={mutateSnapshots}
                />
              )}

              {isFsxResourceType && (
                <FsxSnapshots
                  snapshots={snapshotsWithoutPiTR}
                  entity={entity}
                  selectedSnapshot={selectedSnapshot}
                  setSelectedSnapshot={setSelectedSnapshot}
                  vaults={vaults}
                  mutateSnapshots={mutateSnapshots}
                />
              )}

              {isEfsResourceType && (
                <EfsSnapshots
                  snapshots={snapshotsWithoutPiTR}
                  entity={entity}
                  selectedSnapshot={selectedSnapshot}
                  setSelectedSnapshot={setSelectedSnapshot}
                  vaults={vaults}
                  mutateSnapshots={mutateSnapshots}
                />
              )}

              {/* SnapshotsRows for native backup resources (excluding FSx and EFS) and non-native resources with restore wizard */}
              {((isNativeBackup && !isFsxResourceType && !isEfsResourceType) ||
                (restoreWizardFactory &&
                  !isNativeBackup &&
                  (!snapshotType ||
                    snapshotType === 'rds-based-snapshots'))) && (
                <div className='w-full overflow-auto'>
                  <SnapshotsRows
                    snapshots={snapshotsWithoutPiTR}
                    vaults={vaults}
                    selectedSnapshot={selectedSnapshot}
                    setSelectedSnapshot={setSelectedSnapshot}
                    entity={entity}
                    restoreWizardFactory={restoreWizardFactory}
                    mutateSnapshots={mutateSnapshots}
                  />
                </div>
              )}
              {snapshotType === 'database-pitr' &&
                (isContinuousSupported ? (
                  <ContinuousSnapshotRow
                    resource={entity}
                    selectedDate={dayjs.utc(selectedDate)}
                    dateRange={{
                      start: dayjs.utc(
                        continuousSnapshots?.earliestRestorableTime
                      ),
                      end: dayjs.utc(continuousSnapshots?.latestRestorableTime),
                    }}
                  />
                ) : (
                  restoreWizardFactory && (
                    <PitrSnapshots
                      pitrSnapshots={snapshotsWithPiTR}
                      vaults={vaults}
                      selectedSnapshot={selectedSnapshot}
                      setSelectedSnapshot={setSelectedSnapshot}
                      entity={entity}
                      restoreWizardFactory={restoreWizardFactory}
                      mutateSnapshots={mutateSnapshots}
                    />
                  )
                ))}
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
